import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { takeUIClickEvent } from "../../../../../../common/ga/ga";
import { TakeUIClickEventParameters } from "../../../../../../common/ga/types/ga-general-type";
import { ApplicationEnum } from "../../../../../../constants/constants";
import { fetchCandidateLMSCoursesAsync } from "../../../../redux/onboarding-slice";
import { CORE_URL } from "../../../../utils";
import { selectOnboardingTrainingCourses } from "../../../../redux/selector";
import { debounce, get } from "lodash";
import { selectCandidateProfile } from "../../../../../auth/redux/selector";
import {
  ONBOARDING_TRAINING_COURSES,
  OnboardingTrainingSectionHookReturn,
} from "./onboarding-training-section.types"; // PHKL Custom
import { OnboardingTrainingPlatform } from "../../../course-menu-screen/type";
import { useOnboardingTrainingCommon } from "../../hooks/use-onboarding-training-common";

export const useOnboardingTrainingSection = (): OnboardingTrainingSectionHookReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const onboardingTrainingCourses = useSelector(selectOnboardingTrainingCourses);
  const candidateProfile = useSelector(selectCandidateProfile);
  const [isExpanded, setIsExpanded] = useState(false);

  // Use the common hook for shared logic
  const {
    trainingPlatform,
    onboardingTrainingShouldShow,
    onboardingTrainingIsActive,
    recruitmentConfig,
  } = useOnboardingTrainingCommon();

  // Loading and error states from Redux
  const loading = useSelector(
    (state: any) => state.onboarding.onboardingTrainingCourses?.loading || false,
  );
  const error = useSelector(
    (state: any) => state.onboarding.onboardingTrainingCourses?.error || null,
  );

  // Legacy compatibility
  const onboardingTrainingEnabled = useMemo(() => {
    return onboardingTrainingShouldShow;
  }, [onboardingTrainingShouldShow]);

  useEffect(() => {
    const courseIds =
      recruitmentConfig?.onboardingTraining?.courses?.map((course: any) => course.courseId) || [];

    if (courseIds.length > 0 && candidateProfile?.candidateCode) {
      const debouncedFetch = debounce(
        () => {
          dispatch<any>(fetchCandidateLMSCoursesAsync({ courseIds }));
        },
        300,
        { leading: false, trailing: true },
      );

      debouncedFetch();
      return () => {
        debouncedFetch.cancel();
      };
    }
  }, [dispatch, recruitmentConfig?.onboardingTraining?.courses, candidateProfile]);

  /* PHKL Custom -s*/
  const getCoursesSorting = (course_uid: string) => {
    // if course_uid is not in ONBOARDING_TRAINING_COURSES, give it the largest rank, let it sorted to the end
    const i = ONBOARDING_TRAINING_COURSES.indexOf(course_uid);
    return i === -1 ? ONBOARDING_TRAINING_COURSES.length : i;
  };
  /* PHKL Custom -e*/

  const courses = useMemo(() => {
    /* PHKL Custom -s*/
    /* sorting onboarding training courses
       1. 迎新課程及公司簡介
       2. 理財顧問專業守則及操守風險管理
       3. 「財務需要分析」
       4. 防止清洗黑錢
       5. 反賄賂及反貪污
       6. 熱賣產品
     */
    const coursesList = onboardingTrainingCourses || [];
    return [...coursesList].sort((a: any, b: any) => {
      return getCoursesSorting(a.course_uid) - getCoursesSorting(b.course_uid);
    });
    /* PHKL Custom -e*/
  }, [onboardingTrainingCourses]);

  const displayedCourses = useMemo(() => {
    if (isExpanded || courses.length <= 3) {
      return courses;
    }
    return courses.slice(0, 3);
  }, [courses, isExpanded]);

  const showViewMore = useMemo(() => {
    return courses.length > 3;
  }, [courses]);

  const translations = useMemo(() => {
    return {
      title: t("Recruitment.training"),
      progress: t("Recruitment.training_progress"), // PHKL Custom
      viewMore: t("Common.show_more"),
      viewLess: t("Common.show_less"),
      enroll: t("Recruitment.complete_module_now"),
      inactive: t("Recruitment.training_content_unavailable"),
    };
  }, [t]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  // Create a local clickOnboardingTraining function to avoid circular dependency
  const clickOnboardingTraining = useCallback(async () => {
    const gaData: TakeUIClickEventParameters = {
      module: "Recruitment",
      feature: "OnboardingTraining",
      journey: "feature_entry",
      stage: "journey_entry",
      screen_id: "SCR_RCRTMT_HM",
      screen_name: "Recruitment-Home-Screen",
      object_name: "OnboardingTraining",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);

    if (trainingPlatform === OnboardingTrainingPlatform.COE) {
      // For COE platform, we would need navigation logic here
      // But to avoid circular dependency, we'll just trigger the event for now
      return;
    }

    const application = ApplicationEnum.DOCEBO;
    const redirectUrl = encodeURI(`learn`);
    window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&subscription-key=${get(
      window,
      "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
      "",
    )}&application=${application}&grantType=saml&idp=saam`;
  }, [trainingPlatform]);

  const handleSSOClick = useCallback(
    (courseId?: string) => {
      if (courseId === undefined) {
        // no courseId, means this is an entrance card click, redirect to training catalog
        clickOnboardingTraining();
        return;
      }
      // Find the specific course
      /* PHKL Custom -s*/
      // const course = courses.find((c) => c.courseId === courseId);
      const course = courses.find((c) => c.course_id === courseId);
      /* PHKL Custom -e*/
      if (!course) return;

      const application = ApplicationEnum.DOCEBO;
      let redirectUrl = "";

      // Determine redirect URL based on enrollment status
      if (course.enrollment_status === "not_enrolled") {
        // Redirect to course catalog or general training page
        redirectUrl = "learn";
      } else {
        // Redirect to specific course
        redirectUrl = `learn/course/${course.course_id}`;
      }

      window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&subscription-key=${get(
        window,
        "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
        "",
      )}&application=${application}&grantType=saml&idp=saam`;

      const gaData: TakeUIClickEventParameters = {
        module: "Recruitment",
        feature: "OnboardingTraining",
        journey: "feature_entry",
        stage: "journey_entry",
        screen_id: "SCR_RCRTMT_HM",
        screen_name: "Recruitment-Home-Screen",
        object_name: "OnboardingTraining",
        object_type: "BUTTON",
      };
      takeUIClickEvent(gaData);
    },
    [courses, clickOnboardingTraining],
  );

  return {
    courses,
    displayedCourses,
    loading,
    error,
    isExpanded,
    showViewMore,
    onboardingTrainingEnabled,
    onboardingTrainingShouldShow,
    onboardingTrainingIsActive,
    trainingPlatform,
    handleToggleExpand,
    handleSSOClick,
    translations,
  };
};
